import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LayoutGrid, CalendarClock } from "lucide-react";
import { Panel } from "@/components/inquiries/OverviewCharts";
import { useAuth } from "@/context/AuthContext";
import { isManager } from "@/lib/access";

export const localMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };

const TONE = { blue: "border-blue-200 text-blue-800", green: "border-emerald-200 text-emerald-800", amber: "border-amber-200 text-amber-800",
  red: "border-rose-200 text-rose-700", slate: "border-slate-200 text-slate-800" };

const Tile = ({ label, value, hint, tone, onClick, testid, idx }) => (
  <button type="button" data-testid={testid} onClick={onClick} style={{ animationDelay: `${idx * 50}ms` }}
    className={`home-tile text-left bg-white border rounded-lg p-4 hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow] ${TONE[tone]}`}>
    <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-3xl font-semibold tracking-tight mt-1 mono" data-testid={`${testid}-value`}>{value ?? "—"}</div>
    {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
  </button>
);

const Rollup = ({ title, rows, testid, onRow }) => (
  <Panel title={title} testid={testid}>
    <div className="overflow-x-auto"><table className="w-full text-sm min-w-[480px]">
      <thead className="text-[11px] uppercase tracking-wider text-slate-500"><tr className="border-b border-slate-100">
        <th className="text-left py-2">Name</th><th className="text-right px-2">Due</th><th className="text-right px-2">Filed</th>
        <th className="text-right px-2">Pending</th><th className="text-right px-2">Overdue</th><th className="text-left pl-3 w-[120px]">Progress</th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key} data-testid={`${testid}-row-${r.key}`} onClick={onRow ? () => onRow(r) : undefined} className={`border-b border-slate-50 last:border-0 ${onRow ? "cursor-pointer hover:bg-blue-50/40" : ""}`}>
            <td className="py-2 text-slate-800">{r.label}</td>
            <td className="text-right px-2 mono">{r.total}</td><td className="text-right px-2 mono text-emerald-700">{r.done}</td>
            <td className="text-right px-2 mono text-amber-700">{r.pending}</td>
            <td className={`text-right px-2 mono ${r.overdue ? "text-rose-700 font-semibold" : "text-slate-400"}`}>{r.overdue}</td>
            <td className="pl-3"><div className="flex items-center gap-2"><div className="h-1.5 flex-1 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${r.filed_pct}%` }} /></div><span className="mono text-[11px] text-slate-500 w-8 text-right">{r.filed_pct}%</span></div></td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={6} className="text-center text-slate-500 py-6">Nothing due this month.</td></tr>}
      </tbody>
    </table></div>
  </Panel>
);

const Backlog = ({ rows, total, onOpen }) => (
  <Panel title="Overdue filings" sub={`${total} past due, oldest first`} testid="cc-backlog">
    {rows.length === 0 ? <div className="text-sm text-slate-500 py-6 text-center" data-testid="cc-backlog-empty">Nothing overdue. Well done.</div> : rows.map((r) => (
      <button key={r.id} type="button" data-testid={`cc-backlog-${r.id}`} onClick={onOpen} className="w-full text-left flex items-center gap-3 py-2 border-b border-slate-50 last:border-0 hover:bg-blue-50/40">
        <span className="flex-1 min-w-0"><span className="block truncate text-sm text-slate-900">{r.name}</span><span className="block truncate text-[11px] text-slate-500">{r.entity || "—"} · {r.owner_name || "—"}</span></span>
        <span className="mono text-xs text-rose-700 shrink-0">{r.days_late}d late</span>
      </button>
    ))}
  </Panel>
);

export default function ComplianceOverview() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [month, setMonth] = useState(localMonth());
  const [group, setGroup] = useState("all");
  const [d, setD] = useState(null);
  const load = useCallback(async () => {
    try { setD((await api.get("/compliance/overview", { params: { month, ...(group !== "all" ? { group } : {}) } })).data); } catch (e) { toast.error(formatErr(e)); }
  }, [month, group]);
  useEffect(() => { load(); }, [load]);
  const board = () => nav("/filing-board");
  const register = (q = "") => nav(`/compliance/register${isManager(user) ? "?scope=team" : "?"}${q}`);
  const t = d?.totals;
  return (
    <div data-testid="compliance-overview-page" className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Compliance</div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Compliance Overview</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input data-testid="cc-month" type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="h-9 w-[160px] bg-white" />
          <Select value={group} onValueChange={setGroup}>
            <SelectTrigger data-testid="cc-group" className="h-9 w-[200px] bg-white"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All groups</SelectItem>
              {Object.entries(d?.groups || {}).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Button data-testid="cc-open-board" variant="outline" onClick={board} className="gap-2 bg-white"><LayoutGrid className="w-4 h-4" />Filing board</Button>
          <Button data-testid="cc-open-register" onClick={() => register()} className="gap-2 bg-blue-700 hover:bg-blue-800 text-white"><CalendarClock className="w-4 h-4" />Compliance register</Button>
        </div>
      </div>
      {!t ? <div className="text-sm text-slate-500" data-testid="cc-loading">Loading…</div> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="cc-tiles">
            {[
              { k: "due", label: "Due this month", value: t.due, tone: "blue", hint: "filings falling in this month" },
              { k: "done", label: "Filed", value: t.done, tone: "green", hint: t.filed_pct == null ? "—" : `${t.filed_pct}% complete` },
              { k: "pending", label: "Pending", value: t.pending, tone: "amber", hint: `${t.due_soon} due within 7 days` },
              { k: "overdue", label: "Overdue this month", value: t.overdue, tone: "red", hint: "past due date, not filed" },
              { k: "backlog", label: "All overdue", value: t.backlog, tone: "red", hint: "including earlier months", onClick: () => register("&status=overdue") },
              { k: "invoice", label: "To invoice", value: t.to_invoice, tone: "slate", hint: `₹${(t.to_invoice_total || 0).toLocaleString("en-IN")}`, onClick: () => nav("/compliance/invoices") },
            ].map((x, i) => <Tile key={x.k} idx={i} testid={`cc-tile-${x.k}`} {...x} onClick={x.onClick || board} />)}
          </div>
          <div className="grid xl:grid-cols-3 gap-4">
            <div className="xl:col-span-2"><Rollup title="By compliance group" rows={d.by_group} testid="cc-by-group" onRow={(r) => setGroup(r.key)} /></div>
            <Backlog rows={d.backlog} total={t.backlog} onOpen={() => register("&status=overdue")} />
          </div>
          <div className="grid xl:grid-cols-2 gap-4">
            <Rollup title="By client" rows={d.by_client} testid="cc-by-client" />
            {isManager(user) && <Rollup title="By owner" rows={d.by_owner} testid="cc-by-owner" />}
          </div>
        </>
      )}
    </div>
  );
}
